import { Minus, Package, Plus, Search, Truck, Wrench } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ItemListDrawerSalesOrder from "./ItemListDrawerSalesOrder";
import ItemListDrawerDeliveryOrder from "./ItemListDrawerDeliveryOrder";

const DeliveryScheduleTab = ({ BASE_URL, COMPANY_CODE, DEPARTMENT }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [salesFilter, setSalesFilter] = useState("All");
  const [customerSearch, setCustomerSearch] = useState("");
  const [workOrders, setWorkOrders] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDO, setLoadingDO] = useState(false);
  const [loadingWO, setLoadingWO] = useState(false);
  const [workOrderItemDrawerOpen, setWorkOrderItemDrawerOpen] = useState(false);
  const [selectedSOForWO, setSelectedSOForWO] = useState(null);
  const [fetchingSalesOrderData, setFetchingSalesOrderData] = useState(false);
  const [selectedSOForDO, setSelectedSOForDO] = useState(null);
  const [deliveryItemDrawerOpen, setDeliveryItemDrawerOpen] = useState(false);
  const [soDetails, setSoDetails] = useState([]);
  const [loadingSODetails, setLoadingSODetails] = useState(false);
  const [activeTab, setActiveTab] = useState("items");
  const [pageNumber, setPageNumber] = useState(1);

  // Track which data has been fetched for current order
  const [fetchedData, setFetchedData] = useState({
    items: false,
    delivery: false,
    work: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingSalesOrders();
  }, [pageNumber]);

  const handleNextPage = () => {
    setPageNumber((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  // Lazy load data when tab changes
  useEffect(() => {
    if (!selectedOrder) return;

    if (activeTab === "items" && !fetchedData.items) {
      fetchSalesOrderDetails(selectedOrder.soNo);
    } else if (activeTab === "delivery" && !fetchedData.delivery) {
      fetchDeliveryOrders(selectedOrder.soNo);
    } else if (activeTab === "work" && !fetchedData.work) {
      fetchWorkOrders(selectedOrder.soNo);
    }
  }, [activeTab, selectedOrder]);

  const fetchPendingSalesOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/CRMTaskMainListFilter/${COMPANY_CODE}/DDASH_SOPEND/-/-/-/-/${DEPARTMENT}/1900-01-01/1900-01-01/-/${pageNumber}/-`,
      );
      const data = await response.json();

      const mappedOrders = data.map((item) => ({
        customer: item.acname?.trim(),
        sun: item.Sales_Person,
        sunName: item.salespersonName,
        amount: item.amount,
        soNo: item.so_no,
        quoteNo: item.SO_DOC || "",
        age: item.age,
        status: item.so_status,
        account: item.account,
        deptno: item.deptno,
      }));

      setPendingOrders(mappedOrders);
    } catch (error) {
      console.error("Error fetching pending orders:", error);
      setPendingOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryOrders = async (soNo) => {
    setLoadingDO(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/CRMTaskMainListFilter/${COMPANY_CODE}/DDASH_DOLIST/-/-/-/${soNo}/${DEPARTMENT}/1900-01-01/1900-01-01/-/-/-`,
      );
      const data = await response.json();

      const mappedDOs = data?.map((item) => ({
        customer: item.acname?.trim(),
        salesPerson: item.sales_person,
        salesPersonName: item.salespersonName,
        amount: item.amount,
        doNo: item.enqid,
        salespersonName: item.salespersonName || "N/A",
        age: item.age,
        status: item.Do_status,
      }));

      setDeliveryOrders(mappedDOs);
      setFetchedData((prev) => ({ ...prev, delivery: true }));
    } catch (error) {
      console.error("Error fetching delivery orders:", error);
      setDeliveryOrders([]);
    } finally {
      setLoadingDO(false);
    }
  };

  const fetchWorkOrders = async (soNo) => {
    setLoadingWO(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/CRMTaskMainListFilter/${COMPANY_CODE}/DDASH_WOLIST/-/-/-/${soNo}/${DEPARTMENT}/1900-01-01/1900-01-01/-/-/-`,
      );
      const data = await response.json();

      const mappedWOs = data.map((item) => ({
        customer: item.acname?.trim(),
        amount: item.amount,
        woNo: item.enqid,
        status: item.enq_takenby,
        age: item.age,
        soStatus: item.SO_STATUS,
        quoteNo: item.SO_DOC || "N/A",
      }));

      setWorkOrders(mappedWOs);
      setFetchedData((prev) => ({ ...prev, work: true }));
    } catch (error) {
      console.error("Error fetching work orders:", error);
      setWorkOrders([]);
    } finally {
      setLoadingWO(false);
    }
  };

  const fetchSalesOrderDetails = async (soNo) => {
    setLoadingSODetails(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/DocumentEditDetails/${COMPANY_CODE}/DDASH_SOSTAT/${soNo}/${DEPARTMENT}`,
      );
      const data = await response.json();
      setSoDetails(data || []);
      setFetchedData((prev) => ({ ...prev, items: true }));
    } catch (error) {
      console.error("Error fetching SO details:", error);
      setSoDetails([]);
    } finally {
      setLoadingSODetails(false);
    }
  };

  const handleOrderSelect = (order) => {
    // Reset data if selecting a different order
    if (selectedOrder?.soNo !== order.soNo) {
      setSelectedOrder(order);
      setActiveTab("items"); // Always start with items tab
      setSoDetails([]);
      setDeliveryOrders([]);
      setWorkOrders([]);
      setFetchedData({ items: false, delivery: false, work: false });

      // Only fetch items data immediately (current tab)
      fetchSalesOrderDetails(order.soNo);
    }
  };

  const filteredOrders = pendingOrders?.filter((order) => {
    const matchesSales = salesFilter === "All" || order.sun === salesFilter;

    const searchText = customerSearch.toLowerCase();

    const matchesCustomer = order.customer?.toLowerCase().includes(searchText);

    const matchesSONo = order.soNo?.toString().includes(searchText);

    return matchesSales && (matchesCustomer || matchesSONo);
  });

  const uniqueSalesPersons = [...new Set(pendingOrders?.map((o) => o.sun))];

  const getAgeColorClass = (age) => {
    if (age >= 240) return "text-red-600";
    if (age >= 220) return "text-orange-600";
    return "text-blue-600";
  };

  const handleOpenDO = (doNo) => {
    navigate("/delivery_order", { state: { docNo: doNo, mode: "edit" } });
  };

  const handleNewWorkOrder = () => {
    if (selectedOrder) {
      setSelectedSOForWO(selectedOrder);
      setWorkOrderItemDrawerOpen(true);
    }
  };

  const handleTransferToWorkOrder = (masterData, selectedItems) => {
    navigate("/work_order", {
      state: {
        transferredData: { master: masterData, items: selectedItems },
        mode: "new",
      },
    });
  };

  const handleNewDeliveryOrder = () => {
    if (selectedOrder) {
      setSelectedSOForDO(selectedOrder);
      setDeliveryItemDrawerOpen(true);
    }
  };

  const handleTransferToDeliveryOrder = (masterData, selectedItems) => {
    navigate("/delivery_order", {
      state: {
        transferredData: { master: masterData, items: selectedItems },
        mode: "new",
      },
    });
  };

  const handleOpenWO = (woNo) => {
    navigate("/work_order", { state: { docNo: woNo, mode: "edit" } });
  };

  const tabs = [
    {
      id: "items",
      label: "Items",
      icon: Package,
      count: soDetails?.length || 0,
    },
    {
      id: "delivery",
      label: "Delivery Orders",
      icon: Truck,
      count: deliveryOrders?.length || 0,
    },
    {
      id: "work",
      label: "Work Orders",
      icon: Wrench,
      count: workOrders?.length || 0,
    },
  ];

  return (
    <div className="flex-1 overflow-hidden p-3 gap-3 grid grid-cols-12 bg-gray-50">
      {/* Left Panel - Sales Orders */}
      <div className="col-span-8 bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
        <div className="px-3 py-2 bg-cyan-50 border-b border-cyan-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-cyan-700">
              Sales Orders - Pending
            </h2>
            {/* <span className="bg-cyan-600 text-white px-2 py-0.5 rounded-full text-xs font-medium">
              {filteredOrders?.length || 0}
            </span> */}
          </div>

          <div className="flex items-center gap-3">
            {/* Sales Filter */}
            <select
              className="px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
              value={salesFilter}
              onChange={(e) => setSalesFilter(e.target.value)}
            >
              <option value="All">All Sales</option>
              {uniqueSalesPersons.map((sp) => (
                <option key={sp} value={sp}>
                  {sp}
                </option>
              ))}
            </select>

            {/* Customer Search */}
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                size={14}
              />
              <input
                type="text"
                placeholder="Customer..."
                className="pl-7 pr-3 py-1.5 text-sm w-44 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
            </div>

            {/* Pagination */}
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={handlePrevPage}
                disabled={pageNumber === 1 || loading}
                className={`px-2 py-1 text-xs rounded-md border ${
                  pageNumber === 1 || loading
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Prev
              </button>

              <span className="text-xs text-gray-600 px-1">{pageNumber}</span>

              <button
                onClick={handleNextPage}
                disabled={loading}
                className={`px-2 py-1 text-xs rounded-md border ${
                  loading
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                <p className="text-gray-500 text-sm">Loading orders...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {filteredOrders?.map((order, index) => (
                <div
                  key={index}
                  onClick={() => handleOrderSelect(order)}
                  className={`relative rounded-lg p-2.5 cursor-pointer transition-all border ${
                    selectedOrder?.soNo === order.soNo
                      ? "border-cyan-600 bg-cyan-50 shadow-md"
                      : "border-gray-200 hover:border-cyan-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex justify-between">
                    <h3
                      className="text-xs font-semibold text-cyan-600 mb-1 flex-1"
                      title={order.customer}
                    >
                      {order.customer}
                    </h3>
                    <span
                      className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${getAgeColorClass(
                        order.age,
                      )}`}
                    >
                      {order.age} Days
                    </span>
                  </div>

                  <p className="text-base font-bold text-gray-800 mb-2">
                    {order.amount.toFixed(2)}
                  </p>

                  <div className="border-t pt-1 mt-1 flex items-center justify-between text-[11px] gap-1">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600">SO:</span>
                      <span className="font-semibold text-gray-800">
                        {order.soNo}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* <span className="text-gray-600">QuotNo:</span>
                      <span className="font-semibold text-gray-800">
                        {order.quoteNo || "N/A"}
                      </span> */}
                      <p className="text-[11px] text-gray-500 mb-1">
                        {order.sun}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Tabbed Details */}
      <div className="col-span-4 flex flex-col gap-3">
        {selectedOrder ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full">
            {/* Header with Tabs */}
            <div className="bg-blue-50 border-b border-blue-200">
              <div className="px-3 py-2">
                <h3 className="text-sm font-semibold text-blue-700">
                  Sales Order Details
                  <span className="ml-1 text-xs text-gray-600">
                    (SO #{selectedOrder.soNo})
                  </span>
                </h3>
              </div>

              {/* Tab Navigation */}
              <div className="flex border-t border-blue-200">
                {tabs?.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-all ${
                      activeTab === tab.id
                        ? "bg-white text-blue-700 border-b-2 border-blue-600"
                        : "text-gray-600 hover:bg-blue-100 hover:text-blue-700"
                    }`}
                  >
                    <tab.icon size={14} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full text-[10px] font-semibold">
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-2">
              {/* Items Tab */}
              {activeTab === "items" && (
                <div className="space-y-2">
                  {loadingSODetails ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <p className="text-xs text-gray-400">
                          Loading items...
                        </p>
                      </div>
                    </div>
                  ) : soDetails.length > 0 ? (
                    soDetails.map((item, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-3 bg-white hover:shadow-sm transition-all mb-2"
                      >
                        {/* Description + Code */}
                        <div className="flex gap-2 items-start mb-1">
                          <p
                            className="text-xs font-semibold text-gray-800 leading-snug line-clamp-2 flex-1"
                            title={item.DESCRIPTION}
                          >
                            {item.DESCRIPTION}
                          </p>

                          <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                            {item.CODE}
                          </span>
                        </div>

                        {/* Comments */}
                        {item.COMMENTS && (
                          <div className="flex flex-wrap gap-1 mt-1 mb-2">
                            {item.COMMENTS.split("/").map((part, pIdx) => (
                              <span
                                key={pIdx}
                                className="text-[9px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded"
                              >
                                {part.trim()}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Quantities */}
                        <div className="grid grid-cols-5 gap-2 text-center">
                          <div className="rounded-md bg-gray-100 py-2">
                            <p className="text-[10px] text-gray-500">Order</p>
                            <p className="text-sm font-extrabold text-gray-800">
                              {item["ORDER QTY"]}
                            </p>
                          </div>

                          <div className="rounded-md bg-green-50 py-2">
                            <p className="text-[10px] text-green-600">
                              Delivered
                            </p>
                            <p className="text-sm font-extrabold text-green-700">
                              {item["DELVD QTY"]}
                            </p>
                          </div>

                          <div className="rounded-md bg-red-50 py-2">
                            <p className="text-[10px] text-red-600">Pending</p>
                            <p className="text-sm font-extrabold text-red-700">
                              {item["PEND QTY"]}
                            </p>
                          </div>

                          <div className="rounded-md bg-orange-50 py-2">
                            <p className="text-[10px] text-orange-600">WO</p>
                            <p className="text-sm font-extrabold text-orange-700">
                              {item["WORK_ORD"]}
                            </p>
                          </div>

                          <div className="rounded-md bg-blue-50 py-2">
                            <p className="text-[10px] text-blue-600">Stock</p>
                            <p className="text-sm font-extrabold text-blue-700">
                              {item["CUR_STOCK"]}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-center items-center h-32">
                      <p className="text-xs text-gray-400">No items found</p>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Orders Tab */}
              {activeTab === "delivery" && (
                <div>
                  <div className="flex items-center justify-between mb-2 pb-2 border-b">
                    <h4 className="text-xs font-semibold text-green-700">
                      Delivery Orders
                    </h4>
                    <button
                      onClick={handleNewDeliveryOrder}
                      disabled={!selectedOrder}
                      className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus size={12} /> New DO
                    </button>
                  </div>

                  {loadingDO ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        <p className="text-xs text-gray-400">
                          Loading delivery orders...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {deliveryOrders.length > 0 ? (
                        deliveryOrders.map((order, index) => {
                          const isInvoiced =
                            order.status === "INVOICED" ||
                            order.status === "Invoiced";
                          return (
                            <div
                              key={index}
                              onClick={
                                !isInvoiced
                                  ? () => handleOpenDO(order.doNo)
                                  : undefined
                              }
                              className={`rounded-lg p-2.5 border shadow-sm ${
                                isInvoiced
                                  ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-60"
                                  : "bg-green-50 border-green-200 cursor-pointer hover:bg-green-100"
                              }`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <span
                                  className={`uppercase px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    isInvoiced
                                      ? "bg-gray-200 text-gray-600"
                                      : "bg-red-100 text-red-700"
                                  }`}
                                >
                                  {order.status}
                                </span>
                                <p className="text-base font-bold text-gray-800">
                                  {order.amount.toFixed(2)}
                                </p>
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <p className="text-gray-600">DO No:</p>
                                  <p className="font-semibold text-gray-800">
                                    {order.doNo}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Age:</p>
                                  <p
                                    className={`font-bold ${getAgeColorClass(
                                      order.age,
                                    )}`}
                                  >
                                    {order.age} Days
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex justify-center items-center h-32">
                          <p className="text-xs text-gray-400">
                            No delivery orders found
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Work Orders Tab */}
              {activeTab === "work" && (
                <div>
                  <div className="flex items-center justify-between mb-2 pb-2 border-b">
                    <h4 className="text-xs font-semibold text-orange-700">
                      Work Orders
                    </h4>
                    <button
                      onClick={handleNewWorkOrder}
                      disabled={!selectedOrder}
                      className="flex items-center gap-1 px-2 py-1 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus size={12} /> New WO
                    </button>
                  </div>

                  {loadingWO ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                        <p className="text-xs text-gray-400">
                          Loading work orders...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {workOrders.length > 0 ? (
                        workOrders.map((order, index) => (
                          <div
                            key={index}
                            onClick={() => handleOpenWO(order.woNo)}
                            className="bg-orange-50 rounded-lg p-2.5 border border-orange-200 shadow-sm cursor-pointer hover:bg-orange-100"
                          >
                            <div className="flex justify-between items-center mb-2">
                              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-[10px] font-semibold">
                                {order.status}
                              </span>
                              <p className="text-base font-bold text-gray-800">
                                {order.amount.toFixed(2)}
                              </p>
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div>
                                <p className="text-gray-600">WO No:</p>
                                <p className="font-semibold text-gray-800">
                                  {order.woNo}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Age:</p>
                                <p
                                  className={`font-bold ${getAgeColorClass(
                                    order.age,
                                  )}`}
                                >
                                  {order.age} Days
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Quote No:</p>
                                <p className="font-semibold text-gray-800">
                                  {order.quoteNo}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex justify-center items-center h-32">
                          <p className="text-xs text-gray-400">
                            No work orders found
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-gray-100 rounded-lg shadow-md p-8 flex items-center justify-center border-2 border-dashed border-gray-300">
            <p className="text-gray-500 text-sm">
              Select an order to view details
            </p>
          </div>
        )}
      </div>

      <ItemListDrawerSalesOrder
        selectedOrder={selectedSOForWO}
        drawerOpen={workOrderItemDrawerOpen}
        handleCloseDrawer={() => setWorkOrderItemDrawerOpen(false)}
        onTransferItems={handleTransferToWorkOrder}
        fetchingSalesOrderData={fetchingSalesOrderData}
        BASE_URL={BASE_URL}
        COMPANY_CODE={COMPANY_CODE}
        DEPARTMENT={DEPARTMENT}
      />
      <ItemListDrawerDeliveryOrder
        selectedOrder={selectedSOForDO}
        drawerOpen={deliveryItemDrawerOpen}
        handleCloseDrawer={() => setDeliveryItemDrawerOpen(false)}
        onTransferItems={handleTransferToDeliveryOrder}
        BASE_URL={BASE_URL}
        COMPANY_CODE={COMPANY_CODE}
        DEPARTMENT={DEPARTMENT}
      />
    </div>
  );
};

export default DeliveryScheduleTab;
